import React, { useState, useEffect } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import {
  LayoutDashboard,
  User,
  Building,
  Users,
  MessageSquare,
  Settings,
  HelpCircle,
  LogOut,
  Bell,
} from 'lucide-react';
import { useSupabaseAuth } from '../../contexts/SupabaseAuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarInset,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarProvider,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from '../ui/sidebar';
import { supabase } from '../../lib/supabaseClient';
import ThemeToggle from '../ThemeToggle';

// Feature flag: Set to true to enable messaging system
const ENABLE_MESSAGING = false;

const navItems = [
  { to: '/client', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/client/candidates', icon: Users, label: 'Candidates' },
  { to: '/client/my-details', icon: User, label: 'My Details' },
  { to: '/client/my-organisation', icon: Building, label: 'My Organisation' },
  ...(ENABLE_MESSAGING ? [{ to: '/client/requests', icon: MessageSquare, label: 'Messages' }] : []),
];

const settingsItems = [
  { to: '/client/settings', icon: Settings, label: 'Settings' },
  { to: '/client/help-support', icon: HelpCircle, label: 'Help & Support' },
];

function AppSidebar() {
  const { user, signOut } = useSupabaseAuth();
  const navigate = useNavigate();
  const { setOpenMobile } = useSidebar();
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread messages count (only if messaging is enabled)
  useEffect(() => {
    if (!user || !ENABLE_MESSAGING) return;

    const fetchUnreadCount = async () => {
      try {
        // Get client_id from client_contacts
        const { data: contact } = await supabase
          .from('client_contacts')
          .select('client_id')
          .eq('user_id', user.id)
          .single();

        if (!contact) {
          setUnreadCount(0);
          return;
        }

        // Fetch requests for this client with last read timestamp
        const { data: requests } = await supabase
          .from('candidate_requests')
          .select('id, last_read_by_client_at')
          .eq('client_id', contact.client_id);

        if (!requests || requests.length === 0) {
          setUnreadCount(0);
          return;
        }

        // Count unread messages for each request
        let totalUnread = 0;

        for (const request of requests) {
          const query = supabase
            .from('request_messages')
            .select('*', { count: 'exact', head: true })
            .eq('request_id', request.id)
            .eq('sender_type', 'recruiter');

          // If there's a last read timestamp, only count messages after that
          if (request.last_read_by_client_at) {
            query.gt('created_at', request.last_read_by_client_at);
          }

          const { count } = await query;
          totalUnread += (count || 0);
        }

        setUnreadCount(totalUnread);
      } catch (error) {
        console.error('Error fetching unread messages:', error);
      }
    };

    fetchUnreadCount();

    // Set up real-time subscription for new messages
    const channel = supabase
      .channel('request_messages_changes_client')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'request_messages' },
        () => {
          fetchUnreadCount();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user]);

  const handleLogout = async () => {
    await signOut();
    navigate('/login');
  };

  return (
    <Sidebar>
      <SidebarHeader>
        <div className="flex items-center gap-2 px-2 py-2">
          <img
            src="/appsytec-logo.png"
            alt="Appsytec Logo"
            className="h-8 w-auto object-contain cursor-pointer"
            onClick={() => navigate('/')}
          />
          <div className="flex flex-col flex-1 text-left text-sm leading-tight">
            <span className="truncate font-semibold">Appsytec</span>
            <span className="truncate text-xs text-sidebar-foreground/70">Client Portal</span>
          </div>
        </div>
      </SidebarHeader>

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>Navigation</SidebarGroupLabel>
          <SidebarMenu>
            {navItems.map((item) => (
              <SidebarMenuItem key={item.to}>
                <NavLink to={item.to} end={item.exact} onClick={() => setOpenMobile(false)}>
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive} tooltip={item.label}>
                      <item.icon />
                      <span>{item.label}</span>
                      {item.label === 'Messages' && unreadCount > 0 && (
                        <Badge variant="destructive" className="ml-auto h-5 w-5 flex items-center justify-center p-0 text-xs">
                          {unreadCount}
                        </Badge>
                      )}
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>

        <SidebarSeparator />

        <SidebarGroup>
          <SidebarGroupLabel>Settings</SidebarGroupLabel>
          <SidebarMenu>
            {settingsItems.map((item) => (
              <SidebarMenuItem key={item.to}>
                <NavLink to={item.to} onClick={() => setOpenMobile(false)}>
                  {({ isActive }) => (
                    <SidebarMenuButton isActive={isActive} tooltip={item.label}>
                      <item.icon />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  )}
                </NavLink>
              </SidebarMenuItem>
            ))}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <SidebarMenu>
          <SidebarMenuItem>
            <div className="flex flex-col gap-2 p-2">
              <div className="flex items-center gap-2 text-sm">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-sidebar-primary text-sidebar-primary-foreground">
                  {user?.email?.[0]?.toUpperCase() || 'U'}
                </div>
                <div className="flex flex-col flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-medium">{user?.email?.split('@')[0] || 'User'}</span>
                  <span className="truncate text-xs text-sidebar-foreground/70">Client</span>
                </div>
              </div>
              <Button
                onClick={handleLogout}
                variant="outline"
                size="sm"
                className="w-full justify-start"
              >
                <LogOut className="h-4 w-4 mr-2" />
                <span>Logout</span>
              </Button>
            </div>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}

function Header() {
  const { unreadCount } = useNotifications();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 flex h-16 shrink-0 items-center gap-2 border-b bg-background px-4">
      <div className="flex items-center gap-2">
        <SidebarTrigger className="lg:hidden" />
        <h1 className="text-lg font-semibold">Client Dashboard</h1>
      </div>
      <div className="ml-auto flex items-center gap-2">
        {/* Notification Bell */}
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => navigate('/client/notifications')}
          title="Notifications"
        >
          <Bell className="h-5 w-5" />
          {unreadCount > 0 && (
            <Badge
              variant="destructive"
              className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 text-xs"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
        <ThemeToggle />
      </div>
    </header>
  );
}

const ClientLayout = () => {
  return (
    <SidebarProvider defaultOpen={true}>
      <AppSidebar />
      <SidebarInset>
        <Header />
        <div className="flex flex-1 flex-col gap-3 p-3 sm:gap-4 sm:p-4 md:gap-8 md:p-6 lg:p-8 w-full max-w-full overflow-x-hidden">
          <Outlet />
        </div>
      </SidebarInset>
    </SidebarProvider>
  );
};

export default ClientLayout;
